import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

import { TextField, SelectField } from "./Fields";
import Honeypot from "./Honeypot";
import Turnstile from "./Turnstile";
import { quickSchema, SERVICE_TYPES } from "../../lib/schemas";
import { submitQuickLead } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";

/**
 * "Quote in 60 seconds" — compact hero mini-form.
 * Designed to live on the Home page hero (light theme variant available).
 */
export default function QuickQuoteForm({ light = false }) {
  const [serverError, setServerError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(quickSchema),
    defaultValues: { _hp_check: "" },
  });

  const onSubmit = async (values) => {
    setServerError(null);
    const res = await submitQuickLead({
      ...values,
      turnstile_token: turnstileToken || undefined,
    });
    if (!res.ok) {
      setServerError(res.message || "Something went wrong. Please try again.");
      return;
    }
    trackEvent("generate_lead", {
      lead_type: "quick",
      service_type: values.service_type,
      value: 0,
    });
    setSuccess(true);
    reset();
  };

  if (success) {
    return (
      <div
        className="rounded-md border border-ridge-border bg-white p-7 shadow-[0_24px_48px_-24px_rgba(28,28,28,0.35)] md:p-8"
        data-testid="quick-quote-success"
        role="status"
        aria-live="polite"
      >
        <div className="flex h-11 w-11 items-center justify-center rounded-md bg-ridge-amber/15 text-ridge-amber">
          <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
        </div>
        <h3 className="font-display mt-5 text-xl font-semibold text-ridge-charcoal">
          Got it. We'll reply within the hour.
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-ridge-text">
          During business hours (Mon–Fri 7am–7pm EST). Watch your inbox for a
          confirmation.
        </p>
        <button
          type="button"
          onClick={() => setSuccess(false)}
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-ridge-amber hover:text-ridge-amber-dark"
          data-testid="quick-quote-submit-another"
        >
          Submit another
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="rounded-md border border-ridge-border bg-white p-6 shadow-[0_24px_48px_-24px_rgba(28,28,28,0.35)] md:p-7"
      aria-label="Quick quote request"
      data-testid="quick-quote-form"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold leading-tight text-ridge-charcoal md:text-xl">
            Quick Quote Request
          </h3>
        </div>
      </div>
      <p className="mt-1 text-sm text-ridge-muted">
        A few details and we'll reply within the hour.
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          name="pickup_city"
          label="Pickup City"
          required
          placeholder="e.g., Milton"
          autoComplete="off"
          register={register}
          error={errors.pickup_city?.message}
          testId="quick-pickup-city"
        />
        <TextField
          name="dropoff_city"
          label="Drop-off City"
          required
          placeholder="e.g., Toronto"
          autoComplete="off"
          register={register}
          error={errors.dropoff_city?.message}
          testId="quick-dropoff-city"
        />
        <div className="sm:col-span-2">
          <SelectField
            name="service_type"
            label="Service Type"
            required
            options={SERVICE_TYPES}
            register={register}
            error={errors.service_type?.message}
            testId="quick-service-type"
          />
        </div>
        <TextField
          name="name"
          label="Your Name"
          required
          placeholder="Full name"
          autoComplete="name"
          register={register}
          error={errors.name?.message}
          testId="quick-name"
        />
        <TextField
          name="email"
          label="Email"
          type="email"
          required
          placeholder="you@company.com"
          autoComplete="email"
          register={register}
          error={errors.email?.message}
          testId="quick-email"
        />
        <div className="sm:col-span-2">
          <TextField
            name="phone"
            label="Phone"
            type="tel"
            placeholder="(905) 555-0100"
            hint="Optional"
            autoComplete="tel"
            register={register}
            error={errors.phone?.message}
            testId="quick-phone"
          />
        </div>
      </div>

      <Honeypot register={register} />
      <Turnstile onToken={setTurnstileToken} theme="light" />

      {serverError ? (
        <p
          className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          role="alert"
          data-testid="quick-quote-server-error"
        >
          {serverError}
        </p>
      ) : null}

      <button
        type="submit"
        className="btn-accent mt-5 w-full"
        disabled={isSubmitting}
        data-testid="quick-quote-submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Sending…
          </>
        ) : (
          <>
            Get My Quote
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </>
        )}
      </button>
      <p className="mt-3 text-center text-[11px] text-ridge-muted">
        We'll never share your info. Unsubscribe anytime.
      </p>
    </form>
  );
}
